import React, { useEffect, useState } from 'react';
import {
  Card, Form, Input, Button, Space, Alert, Descriptions,
  Tabs, message, Divider, Select, Switch
} from 'antd';
import {
  SaveOutlined, CreditCardOutlined, SettingOutlined,
  CheckCircleOutlined, MobileOutlined
} from '@ant-design/icons';
import '@/styles/global.css';

const { Option } = Select;
const { TextArea } = Input;

const PaymentConfigManager: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [configForm] = Form.useForm();

  // 支付方式配置（默认微信开启，其余关闭）
  const defaultGateway = {
    // 微信支付
    wechatEnabled: true,
    wechatAppId: '', // 支付 AppID（用于创建支付订单）
    miniprogramAppId: '', // 小程序 AppID（用于获取 openid，可能和支付 AppID 不同）
    miniprogramAppSecret: '', // 小程序 AppSecret（用于获取 openid）
    wechatMerchantId: '',
    wechatApiKey: '',
    wechatApiV3Key: '',
    wechatSerialNo: '',
    wechatPrivateKey: '',
    // 微信支付场景配置
    jsapiAuthorizeDir: '',
    nativeNotifyDomain: '',
    h5WapUrl: '',
    h5WapName: '',
    defaultScene: 'auto',
    // 支付宝
    alipayEnabled: false,
    alipayAppId: '',
    alipayPrivateKey: '',
    alipayPublicKey: '',
    // 聚合支付
    xunhuEnabled: false,
    xunhuAppId: '',
    xunhuAppSecret: '',
    xunhuNotifyUrl: `${window.location.origin}/api/payment/callback/xunhu`,
    xunhuApiUrl: 'https://api.xunhupay.com/payment/do.html',
    // PAYJS（个人微信）
    payjsEnabled: false,
    payjsMchId: '',
    payjsKey: '',
    payjsNotifyUrl: `${window.location.origin}/api/payment/callback/payjs`,
    payjsApiBase: 'https://payjs.cn/api',
    // Stripe
    stripeEnabled: false,
    stripePublicKey: '',
    stripeSecretKey: '',
    // PayPal
    paypalEnabled: false,
    paypalClientId: '',
    paypalSecret: '',
    // 回调URL配置
    notifyUrl: `${window.location.origin}/api/payment/callback`,
    returnUrl: `${window.location.origin}/payment/success`,
  }

  // 加载支付配置数据
  useEffect(() => {
    let mounted = true
    
    const loadPaymentData = async () => {
      try {
        const { apiClient } = await import('@/services/api')
        
        // 加载支付网关配置
        const configResp = await apiClient.get('/admin/payment/config')
        if (configResp?.success && mounted) {
          const data = configResp.data || {}
          configForm.setFieldsValue({
            ...defaultGateway,
            ...data,
          })
        }
      } catch (error) {
        console.warn('加载支付配置失败:', error)
        // 使用默认配置
        if (mounted) {
        configForm.setFieldsValue({ ...defaultGateway })
      }
      }
    }
    
    loadPaymentData()
    return () => { mounted = false }
  }, [])

  // 保存支付配置
  const handleSaveConfig = async (values: any) => {
    try {
      setLoading(true);
      
      // 获取表单中所有字段的值（包括未显示的 Tab 中的字段）
      // 这样可以确保提交所有配置，不会丢失任何字段
      const allFormValues = configForm.getFieldsValue(true); // true 表示包含隐藏字段
      
      // 合并 values（当前提交的字段）和 allFormValues（所有表单字段）
      // values 中的字段优先（用户当前修改的）
      const configToSubmit = {
        ...allFormValues,
        ...values,
      };
      
      console.log('💾 保存支付配置:', {
        提交的字段数: Object.keys(values).length,
        所有表单字段数: Object.keys(allFormValues).length,
        最终提交字段数: Object.keys(configToSubmit).length
      });
      
      const { apiClient } = await import('@/services/api')
      const resp = await apiClient.post('/admin/payment/config', configToSubmit)
      if (!resp?.success) throw new Error(resp?.message || '保存失败')
      console.log('✅ 配置保存成功:', resp);
      message.success('支付配置已保存，前台支付页面将立即生效');
      // 手动触发configStore刷新，确保立即同步
      const { useConfigStore } = await import('@/store/configStore')
      await useConfigStore.getState().load()
      console.log('✅ 配置已刷新到 configStore');
      
      // 重新加载配置表单数据（从服务器获取最新配置）
      const configResp = await apiClient.get('/admin/payment/config');
      if (configResp?.success && configResp.data) {
        configForm.setFieldsValue({
          ...defaultGateway,
          ...configResp.data,
        });
      }
    } catch (error: any) {
      console.error('❌ 保存失败:', error);
      message.error('保存失败: ' + (error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 测试支付配置
  const handleTestPayment = async (provider: 'wechat' | 'alipay' | 'xunhu' | 'payjs') => {
    try {
      setLoading(true);
      console.log('🧪 测试支付配置:', provider);
      const { apiClient } = await import('@/services/api');
      const resp = await apiClient.get(`/admin/payment/test/${provider}`);
      
      if (resp?.success && resp.data?.available) {
        const providerNames: Record<string, string> = {
          wechat: '微信',
          alipay: '支付宝',
          xunhu: '聚合',
          payjs: 'PAYJS'
        };
        message.success(resp.data.message || `${providerNames[provider] || provider}支付配置正确！`);
      } else {
        message.error(resp.data?.message || '配置测试失败');
      }
    } catch (error: any) {
      console.error('❌ 测试失败:', error);
      message.error('测试失败: ' + (error?.response?.data?.message || error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center space-x-3">
        <CreditCardOutlined className="text-2xl text-purple-600" />
        <h2 className="text-2xl font-bold text-gray-800">支付配置管理</h2>
      </div>

      <Alert
        message="支付配置说明"
        description="管理支付网关配置。修改后将立即对用户生效，请谨慎操作。"
        type="warning"
        showIcon
        closable
      />

      {/* 支付网关配置卡片 */}
      {renderGatewayConfig()}
    </div>
  );

  // 渲染支付网关配置
  function renderGatewayConfig() {
    return (
      <Card 
        title={
          <span className="flex items-center gap-2">
            <SettingOutlined className="text-blue-600 text-xl" />
            <span className="text-lg font-semibold text-gray-800">支付网关配置</span>
          </span>
        }
        className="shadow-lg border border-gray-100 bg-white"
        bodyStyle={{ padding: '24px' }}
      >
        <Form
          form={configForm}
          layout="vertical"
          onFinish={handleSaveConfig}
          initialValues={{
            ...defaultGateway
          }}
        >
          <Tabs 
            defaultActiveKey="wechat" 
            type="card"
            size="large"
            style={{
              marginTop: '8px'
            }}
            tabBarStyle={{
              marginBottom: '24px',
              backgroundColor: 'transparent'
            }}
            className="payment-gateway-tabs"
            items={[
              {
                key: 'wechat',
                label: (
                  <span className="flex items-center gap-2">
                    <CreditCardOutlined />
                    <span>微信支付</span>
                  </span>
                ),
                children: (
                  <Card size="small" className="shadow-sm bg-gray-50 border-0 mt-2">
                    <Form.Item
                      label="启用微信支付"
                      name="wechatEnabled"
                      valuePropName="checked"
                    >
                <Switch checkedChildren="开启" unCheckedChildren="关闭" defaultChecked />
              </Form.Item>
              <Form.Item 
                label="支付 AppID" 
                name="wechatAppId"
                tooltip="用于创建支付订单的 AppID（通常与商户号绑定的 AppID）"
              >
                <Input placeholder="wx1234567890abcdef" />
              </Form.Item>
              <Form.Item label="商户号" name="wechatMerchantId">
                <Input placeholder="1234567890" />
              </Form.Item>
              <Form.Item label="API密钥（V2）" name="wechatApiKey">
                <Input.Password placeholder="32位密钥字符串" />
              </Form.Item>
              <Form.Item label="API V3密钥" name="wechatApiV3Key">
                <Input.Password placeholder="32位密钥字符串" />
              </Form.Item>
              <Form.Item label="证书序列号" name="wechatSerialNo">
                <Input placeholder="证书序列号" />
              </Form.Item>
              <Form.Item label="商户私钥（PEM格式）" name="wechatPrivateKey">
                <TextArea 
                  rows={4} 
                        placeholder="-----BEGIN PRIVATE KEY-----\nMIIEvQIBADANBgkqhkiG9w0...\n-----END PRIVATE KEY-----" 
                />
              </Form.Item>
                    
                    {/* 场景配置 */}
                    <Divider orientation="left">支付场景配置</Divider>
                    
                    <Alert
                      type="info"
                      message="多场景支持"
                      description="配置不同场景的支付参数，系统会根据客户端类型自动选择合适的支付方式"
                      showIcon
                      className="mb-3"
                    />
                    
                    <Form.Item 
                      label="默认支付场景" 
                      name="defaultScene"
                      tooltip="当无法自动检测时使用的默认场景"
                    >
                      <Select>
                        <Option value="auto">自动检测</Option>
                        <Option value="native">Native 扫码（PC端）</Option>
                        <Option value="jsapi">JSAPI（微信内）</Option>
                        <Option value="h5">H5（移动浏览器）</Option>
                      </Select>
                    </Form.Item>
                    
                    <Form.Item 
                      label="JSAPI 授权目录" 
                      name="jsapiAuthorizeDir"
                      tooltip="微信公众号支付授权目录，必须以 / 结尾"
                      rules={[
                        {
                          pattern: /^https?:\/\/.+\/$/,
                          message: '必须是有效的URL且以 / 结尾'
                        }
                      ]}
                    >
                      <Input 
                        placeholder="https://jspay.duo123.net/" 
                        addonBefore="JSAPI"
                      />
                    </Form.Item>
                    
                    <Form.Item 
                      label="Native 回调域名" 
                      name="nativeNotifyDomain"
                      tooltip="Native 扫码支付的回调域名"
                      rules={[
                        {
                          pattern: /^https?:\/\/.+/,
                          message: '必须是有效的URL'
                        }
                      ]}
                    >
                      <Input 
                        placeholder="https://api.duo123.net" 
                        addonBefore="Native"
                      />
                    </Form.Item>
                    
                    <Form.Item 
                      label="H5 WAP 网站 URL" 
                      name="h5WapUrl"
                      tooltip="H5 支付的 WAP 网站地址"
                      rules={[
                        {
                          pattern: /^https?:\/\/.+/,
                          message: '必须是有效的URL'
                        }
                      ]}
                    >
                      <Input 
                        placeholder="https://sora.duo123.net" 
                        addonBefore="H5"
                      />
                    </Form.Item>
                    
                    <Form.Item 
                      label="H5 WAP 网站名称" 
                      name="h5WapName"
                      tooltip="显示在微信支付页面的网站名称"
                    >
                      <Input placeholder="Sora3 AI Platform" />
                    </Form.Item>
                    
                    {/* 场景配置说明 */}
                    <Card size="small" className="bg-gray-50 mb-3">
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Native（扫码）">
                          PC端用户扫码支付，需配置回调域名
                        </Descriptions.Item>
                        <Descriptions.Item label="JSAPI（微信内）">
                          在微信内H5或小程序支付，需配置授权目录
                        </Descriptions.Item>
                        <Descriptions.Item label="H5（移动浏览器）">
                          移动浏览器中跳转微信支付，需配置WAP信息
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                    
              <Button
                type="dashed"
                icon={<CheckCircleOutlined />}
                onClick={() => handleTestPayment('wechat')}
                loading={loading}
                block
              >
                测试微信支付配置
              </Button>
            </Card>
                )
              },
              {
                key: 'payjs',
                label: (
                  <span className="flex items-center gap-2">
                    <CreditCardOutlined />
                    <span>PAYJS（个人微信）</span>
                  </span>
                ),
                children: (
                  <Card size="small" className="shadow-sm bg-gray-50 border-0 mt-2">
                    <Alert
                      type="info"
                      message="PAYJS 个人微信 JSAPI"
                      description="仅支持微信内 JSAPI 支付，需提供 openid。PC/非微信场景建议走扫码模式（后续可扩展）。"
                      showIcon
                      className="mb-4"
                    />
                    <Form.Item
                      label="启用 PAYJS"
                      name="payjsEnabled"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    </Form.Item>
                    <Form.Item label="商户号 mchid" name="payjsMchId">
                      <Input placeholder="mchid" />
                    </Form.Item>
                    <Form.Item label="密钥 key" name="payjsKey">
                      <Input.Password placeholder="商户密钥（将加密存储）" />
                    </Form.Item>
                    <Form.Item 
                      label="回调通知URL" 
                      name="payjsNotifyUrl"
                      rules={[{ pattern: /^https?:\/\/.+/, message: '必须是有效的URL' }]}
                    >
                      <Input placeholder={`${window.location.origin}/api/payment/callback/payjs`} />
                    </Form.Item>
                    <Form.Item 
                      label="API Base" 
                      name="payjsApiBase"
                      tooltip="默认 https://payjs.cn/api"
                      rules={[{ pattern: /^https?:\/\/.+/, message: '必须是有效的URL' }]}
                    >
                      <Input placeholder="https://payjs.cn/api" />
                    </Form.Item>
                    <Card size="small" className="bg-gray-50 mb-3">
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="文档地址">
                          <a href="https://payjs.cn/docs/api/jsapi.html" target="_blank" rel="noopener noreferrer">
                            https://payjs.cn/docs/api/jsapi.html
                          </a>
                        </Descriptions.Item>
                        <Descriptions.Item label="适用场景">
                          微信内 H5 页面支付（需 openid）
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                    <Button
                      type="dashed"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleTestPayment('payjs')}
                      loading={loading}
                      block
                    >
                      测试 PAYJS 配置
                    </Button>
                  </Card>
                )
              },
              {
                key: 'alipay',
                label: (
                  <span className="flex items-center gap-2">
                    <CreditCardOutlined />
                    <span>支付宝</span>
                  </span>
                ),
                children: (
                  <Card size="small" className="shadow-sm bg-gray-50 border-0 mt-2">
              <Form.Item
                label="启用支付宝"
                name="alipayEnabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
              </Form.Item>
              <Form.Item label="App ID" name="alipayAppId">
                <Input placeholder="2021..." />
              </Form.Item>
              <Form.Item label="私钥" name="alipayPrivateKey">
                <TextArea rows={3} placeholder="私钥内容..." />
              </Form.Item>
                    <Form.Item label="公钥" name="alipayPublicKey">
                      <TextArea rows={3} placeholder="公钥内容..." />
              </Form.Item>
              <Button
                type="dashed"
                icon={<CheckCircleOutlined />}
                onClick={() => handleTestPayment('alipay')}
                loading={loading}
                block
              >
                测试支付宝配置
              </Button>
            </Card>
                )
              },
              {
                key: 'xunhu',
                label: (
                  <span className="flex items-center gap-2">
                    <CreditCardOutlined />
                    <span>聚合支付</span>
                  </span>
                ),
                children: (
                  <Card size="small" className="shadow-sm bg-gray-50 border-0 mt-2">
                    <Alert
                      type="info"
                      message="聚合支付（虎皮椒）"
                      description="聚合支付是第三方支付平台，支持个人用户快速接入。配置后可用于H5支付场景。"
                      showIcon
                      className="mb-4"
                    />
                    <Form.Item
                      label="启用聚合支付"
                      name="xunhuEnabled"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    </Form.Item>
                    <Form.Item 
                      label="AppID" 
                      name="xunhuAppId"
                      tooltip="聚合支付平台分配的AppID"
                    >
                      <Input placeholder="202303140232" />
                    </Form.Item>
                    <Form.Item 
                      label="AppSecret（密钥）" 
                      name="xunhuAppSecret"
                      tooltip="聚合支付平台分配的密钥，用于签名验证"
                    >
                      <Input.Password placeholder="32位密钥字符串" />
                    </Form.Item>
                    <Form.Item 
                      label="回调通知URL" 
                      name="xunhuNotifyUrl"
                      tooltip="支付成功后聚合支付会回调此URL，通常不需要修改"
                      rules={[
                        {
                          pattern: /^https?:\/\/.+/,
                          message: '必须是有效的URL'
                        }
                      ]}
                    >
                      <Input placeholder="https://api.duo123.net/api/payment/callback/xunhu" />
                    </Form.Item>
                    <Form.Item 
                      label="API地址" 
                      name="xunhuApiUrl"
                      tooltip="聚合支付API接口地址，默认使用官方地址"
                      rules={[
                        {
                          pattern: /^https?:\/\/.+/,
                          message: '必须是有效的URL'
                        }
                      ]}
                    >
                      <Input placeholder="https://api.xunhupay.com/payment/do.html" />
                    </Form.Item>
                    <Card size="small" className="bg-gray-50 mb-3">
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="支付类型">
                          H5支付（移动浏览器）
                        </Descriptions.Item>
                        <Descriptions.Item label="文档地址">
                          <a href="https://www.xunhupay.com/doc/api/pay.html" target="_blank" rel="noopener noreferrer">
                            https://www.xunhupay.com/doc/api/pay.html
                          </a>
                        </Descriptions.Item>
                        <Descriptions.Item label="适用场景">
                          移动端H5网页支付，用户可在手机浏览器中完成支付
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                    <Button
                      type="dashed"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleTestPayment('xunhu')}
                      loading={loading}
                      block
                    >
                      测试聚合支付配置
                    </Button>
                  </Card>
                )
              },
              {
                key: 'stripe',
                label: (
                  <span className="flex items-center gap-2">
                    <CreditCardOutlined />
                    <span>Stripe</span>
                  </span>
                ),
                children: (
                  <Card size="small" className="shadow-sm bg-gray-50 border-0 mt-2">
              <Form.Item
                label="启用 Stripe"
                name="stripeEnabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
              </Form.Item>
              <Form.Item
                label="Stripe Public Key"
                name="stripePublicKey"
              >
                <Input.Password placeholder="pk_test_..." />
              </Form.Item>
              <Form.Item
                label="Stripe Secret Key"
                name="stripeSecretKey"
              >
                <Input.Password placeholder="sk_test_..." />
              </Form.Item>
            </Card>
                )
              },
              {
                key: 'paypal',
                label: (
                  <span className="flex items-center gap-2">
                    <CreditCardOutlined />
                    <span>PayPal</span>
                  </span>
                ),
                children: (
                  <Card size="small" className="shadow-sm bg-gray-50 border-0 mt-2">
              <Form.Item
                label="启用 PayPal"
                name="paypalEnabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
              </Form.Item>
              <Form.Item
                label="PayPal Client ID"
                name="paypalClientId"
              >
                <Input placeholder="client_id_..." />
              </Form.Item>
              <Form.Item
                label="PayPal Secret"
                name="paypalSecret"
              >
                <Input.Password placeholder="secret_..." />
              </Form.Item>
            </Card>
                )
              },
              {
                key: 'miniprogram',
                label: (
                  <span className="flex items-center gap-2">
                    <MobileOutlined />
                    <span>小程序配置</span>
                  </span>
                ),
                children: (
                  <Card size="small" className="shadow-sm bg-gray-50 border-0 mt-2">
                    <Alert
                      type="info"
                      message="小程序配置说明"
                      description="配置小程序 AppID 和 AppSecret，用于获取用户 openid。如果小程序 AppID 与支付 AppID 不同，请在此单独配置。"
                      showIcon
                      className="mb-4"
                    />
                    
                    <Form.Item 
                      label="小程序 AppID" 
                      name="miniprogramAppId"
                      tooltip="用于获取 openid 的小程序 AppID（如果与支付 AppID 不同，请填写）"
                      rules={[
                        {
                          pattern: /^wx[a-zA-Z0-9]+$/,
                          message: '小程序 AppID 格式不正确，应以 wx 开头'
                        }
                      ]}
                    >
                      <Input placeholder="wx1234567890abcdef（如果与支付 AppID 不同）" />
                    </Form.Item>
                    
                    <Form.Item 
                      label="小程序 AppSecret" 
                      name="miniprogramAppSecret"
                      tooltip="小程序的 AppSecret，用于获取 openid。必须与小程序 AppID 配对。"
                      rules={[
                        {
                          required: false,
                          message: '建议填写小程序 AppSecret'
                        }
                      ]}
                    >
                      <Input.Password placeholder="小程序 AppSecret（将加密存储）" />
                    </Form.Item>
                    
                    <Card size="small" className="bg-gray-50 mb-3">
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="用途">
                          用于小程序登录和获取用户 openid
                        </Descriptions.Item>
                        <Descriptions.Item label="获取位置">
                          微信公众平台 → 小程序 → 开发 → 开发管理 → 开发设置
                        </Descriptions.Item>
                        <Descriptions.Item label="注意事项">
                          <div>
                            <div>• 如果小程序 AppID 与支付 AppID 相同，可以不填写小程序 AppID</div>
                            <div>• 小程序 AppSecret 建议填写，用于安全获取 openid</div>
                            <div>• 如果未配置，系统会使用环境变量 WECHAT_APP_ID 和 WECHAT_APP_SECRET</div>
                          </div>
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Card>
                )
              }
            ]}
          />

          <Divider />

          <div className="flex justify-center">
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              size="large"
              className="px-8"
            >
              保存配置
            </Button>
          </div>
        </Form>
      </Card>
    );
  }
};

export default PaymentConfigManager;